import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Page,
  Card,
  IndexTable,
  IndexFilters,
  useSetIndexFiltersMode,
  IndexFiltersMode,
  Badge,
  Text,
  Button,
  EmptyState,
  Banner,
  ChoiceList,
  InlineStack,
  InlineGrid,
  Modal,
  BlockStack,
  type TabProps,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import { useStore } from '../providers/StoreProvider';
import { campaignEditPath } from '../lib/campaignEditor';
import { ActivateAppBanner } from '../components/ActivateAppBanner';
import { StatCard } from '../components/StatCard';
import { t } from '../i18n';

interface CampaignStats {
  revenue: number;
  atc: number;
  used: number;
}

interface CampaignRow {
  id: string;
  name: string;
  type: string;
  status: string;
  priority: number;
  usageCount: number;
  maxUsesTotal?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  tags?: string[];
  config?: Record<string, unknown>;
  stats?: CampaignStats;
  [key: string]: unknown;
}

/** IndexFilters tab ids — Shopify products-style views */
type StatusView = 'all' | 'active' | 'scheduled' | 'inactive';

const statusTone = (
  s: string,
): 'success' | 'warning' | 'info' | 'critical' | undefined => {
  if (s === 'ACTIVE') return 'success';
  if (s === 'SCHEDULED') return 'info';
  if (s === 'PAUSED') return 'warning';
  if (s === 'ARCHIVED' || s === 'EXPIRED' || s === 'INACTIVE') return 'critical';
  return undefined;
};

/** Effective UI status from DB status + schedule window */
function effectiveStatus(c: CampaignRow): string {
  const now = Date.now();
  const starts = c.startsAt ? new Date(c.startsAt).getTime() : null;
  const ends = c.endsAt ? new Date(c.endsAt).getTime() : null;

  if (c.status === 'ARCHIVED') return 'ARCHIVED';
  if (c.status === 'PAUSED') return 'PAUSED';
  if (c.status === 'EXPIRED') return 'EXPIRED';
  if (ends != null && Number.isFinite(ends) && ends < now) return 'EXPIRED';
  if (c.status === 'SCHEDULED') return 'SCHEDULED';
  if (starts != null && Number.isFinite(starts) && starts > now) {
    return 'SCHEDULED';
  }
  if (c.status === 'DRAFT' || c.status === 'ACTIVE') return 'ACTIVE';
  return c.status || 'INACTIVE';
}

function statusLabel(s: string): string {
  if (s === 'DRAFT' || s === 'ACTIVE') return t('Active');
  if (s === 'SCHEDULED') return t('Scheduled');
  if (s === 'EXPIRED') return t('Expired');
  if (s === 'PAUSED' || s === 'ARCHIVED') return t('Inactive');
  return s;
}

function typeLabel(type: string): string {
  if (type === 'FREE_GIFT') return t('Free gift');
  if (type === 'BUY_X_GET_Y') return t('Buy X Get Y');
  if (type === 'CART_GOAL' || type === 'MILESTONE') return t('Milestone Rewards');
  return type.replace(/_/g, ' ');
}

/** Total use cap from column + advanced config (null = unlimited). */
function resolveMaxUsesTotal(c: CampaignRow): number | null {
  const advanced = (c.config?.advanced || {}) as {
    usageLimitEnabled?: boolean;
    totalUses?: number | null;
  };
  if (advanced.usageLimitEnabled === false) return null;
  if (
    advanced.usageLimitEnabled === true &&
    advanced.totalUses != null &&
    Number(advanced.totalUses) > 0
  ) {
    return Math.floor(Number(advanced.totalUses));
  }
  if (c.maxUsesTotal != null && Number(c.maxUsesTotal) > 0) {
    return Math.floor(Number(c.maxUsesTotal));
  }
  return null;
}

/**
 * Campaign auto-stopped because Limit total uses was reached.
 * Activate/Pause is frozen for these — merchant must raise the limit in Edit.
 */
function isUsageLimitExhausted(c: CampaignRow): boolean {
  if (c.status === 'EXPIRED') {
    // Auto-expire from usage cap (or schedule) — freeze activate when a
    // total-use limit was configured and usage has hit it.
    const max = resolveMaxUsesTotal(c);
    if (max != null && max > 0) {
      const used = Number(c.usageCount) || 0;
      if (used >= max) return true;
    }
    // EXPIRED from auto-usage even if usageCount lag: still freeze
    if (max != null && max > 0) return true;
  }
  const max = resolveMaxUsesTotal(c);
  if (max == null || max <= 0) return false;
  return (Number(c.usageCount) || 0) >= max;
}

function matchesView(c: CampaignRow, view: StatusView): boolean {
  if (view === 'all') return true;
  const eff = effectiveStatus(c);
  if (view === 'active') return eff === 'ACTIVE';
  if (view === 'scheduled') return eff === 'SCHEDULED';
  return (
    eff === 'PAUSED' ||
    eff === 'EXPIRED' ||
    eff === 'ARCHIVED' ||
    eff === 'INACTIVE'
  );
}

function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (Math.abs(n) >= 1000) return n.toLocaleString();
  return String(Math.round(n));
}

const VIEW_IDS: StatusView[] = ['all', 'active', 'scheduled', 'inactive'];

export function CampaignsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { formatMoney } = useStore();
  const [items, setItems] = useState<CampaignRow[]>([]);
  const [query, setQuery] = useState('');
  const [queryValue, setQueryValue] = useState('');
  const [selectedView, setSelectedView] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignRow | null>(null);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  // Debounce search typing → API query
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(queryValue), 250);
    return () => window.clearTimeout(t);
  }, [queryValue]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set('search', query);
    params.set('limit', '100');
    const qs = params.toString();
    api
      .get<{ items: CampaignRow[]; total?: number }>(
        `/campaigns${qs ? `?${qs}` : ''}`,
      )
      .then((r) => {
        const list = Array.isArray(r) ? r : r?.items || [];
        setItems(list as CampaignRow[]);
      })
      .catch((err: Error) => {
        console.error('Failed to load campaigns', err);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [api, query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const statusView = VIEW_IDS[selectedView] || 'all';

  const filtered = useMemo(() => {
    let list = items.filter((c) => matchesView(c, statusView));
    // Extra Status filter (Active / Scheduled / Inactive) from filter popover
    if (statusFilter.length) {
      list = list.filter((c) => {
        const eff = effectiveStatus(c);
        return statusFilter.some((f) => {
          if (f === 'active') return eff === 'ACTIVE';
          if (f === 'scheduled') return eff === 'SCHEDULED';
          if (f === 'inactive') {
            return (
              eff === 'PAUSED' ||
              eff === 'EXPIRED' ||
              eff === 'ARCHIVED' ||
              eff === 'INACTIVE'
            );
          }
          return false;
        });
      });
    }
    if (typeFilter.length) {
      list = list.filter((c) => typeFilter.includes(c.type));
    }
    return list;
  }, [items, statusView, statusFilter, typeFilter]);

  const tabCounts = useMemo(() => {
    const counts = { all: 0, active: 0, scheduled: 0, inactive: 0 };
    for (const c of items) {
      counts.all += 1;
      if (matchesView(c, 'active')) counts.active += 1;
      else if (matchesView(c, 'scheduled')) counts.scheduled += 1;
      else if (matchesView(c, 'inactive')) counts.inactive += 1;
    }
    return counts;
  }, [items]);

  const summary = useMemo(() => {
    let revenue = 0;
    let atc = 0;
    let used = 0;
    for (const c of filtered) {
      revenue += Number(c.stats?.revenue) || 0;
      atc += Number(c.stats?.atc) || 0;
      used += Number(c.stats?.used ?? c.usageCount) || 0;
    }
    return { revenue, atc, used, count: filtered.length };
  }, [filtered]);

  async function pauseOrResume(c: CampaignRow) {
    if (isUsageLimitExhausted(c)) {
      setActionError(
        'This campaign reached its total use limit. Edit the campaign to raise the limit before activating.',
      );
      return;
    }
    setActionError(null);
    setActionMessage(null);
    setActionId(c.id);
    try {
      const eff = effectiveStatus(c);
      if (eff === 'PAUSED' || c.status === 'PAUSED') {
        await api.post(`/campaigns/${c.id}/activate`);
        setActionMessage(`“${c.name}” is now active`);
      } else {
        await api.post(`/campaigns/${c.id}/pause`);
        setActionMessage(`“${c.name}” paused`);
      }
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setActionError(null);
    setActionMessage(null);
    setActionId(deleteTarget.id);
    try {
      await api.del(`/campaigns/${deleteTarget.id}`);
      setActionMessage(`“${deleteTarget.name}” deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setActionId(null);
    }
  }

  // Shopify IndexFilters tabs (view switcher)
  const tabs: TabProps[] = [
    {
      id: 'all',
      content: t("All"),
      badge: String(tabCounts.all),
      isLocked: true,
    },
    {
      id: 'active',
      content: t("Active"),
      badge: String(tabCounts.active),
    },
    {
      id: 'scheduled',
      content: t("Scheduled"),
      badge: String(tabCounts.scheduled),
    },
    {
      id: 'inactive',
      content: t("Inactive"),
      badge: String(tabCounts.inactive),
    },
  ];

  const typeChoices = useMemo(() => {
    const types = Array.from(new Set(items.map((c) => c.type))).sort();
    return types.map((t) => ({
      label: t.replace(/_/g, ' '),
      value: t,
    }));
  }, [items]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title={t("Status")}
          titleHidden
          choices={[
            { label: t("Active"), value: 'active' },
            { label: t("Scheduled"), value: 'scheduled' },
            { label: t("Inactive"), value: 'inactive' },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple
        />
      ),
      shortcut: true,
      pinned: true,
    },
    {
      key: 'type',
      label: 'Type',
      filter: (
        <ChoiceList
          title={t("Type")}
          titleHidden
          choices={
            typeChoices.length
              ? typeChoices
              : [{ label: t("No types yet"), value: '_none', disabled: true }]
          }
          selected={typeFilter}
          onChange={setTypeFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: {
    key: string;
    label: string;
    onRemove: () => void;
  }[] = [];

  if (statusFilter.length) {
    const labels = statusFilter.map(
      (s) => s.charAt(0).toUpperCase() + s.slice(1),
    );
    appliedFilters.push({
      key: 'status',
      label: `Status: ${labels.join(', ')}`,
      onRemove: () => setStatusFilter([]),
    });
  }
  if (typeFilter.length) {
    appliedFilters.push({
      key: 'type',
      label: `Type: ${typeFilter.map((t) => t.replace(/_/g, ' ')).join(', ')}`,
      onRemove: () => setTypeFilter([]),
    });
  }

  function handleFiltersClearAll() {
    setStatusFilter([]);
    setTypeFilter([]);
    setQueryValue('');
    setQuery('');
  }

  return (
    <Page
      title={t("Campaigns")}
      primaryAction={{
        content: t("Create campaign"),
        onAction: () => navigate('/campaigns/new'),
      }}
    >
      <BlockStack gap="400">
        <ActivateAppBanner />

        {actionError && (
          <Banner tone="critical" onDismiss={() => setActionError(null)}>
            {actionError}
          </Banner>
        )}
        {actionMessage && (
          <Banner tone="success" onDismiss={() => setActionMessage(null)}>
            {actionMessage}
          </Banner>
        )}

        <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
          <StatCard
            title={t("Campaigns")}
            value={loading ? '…' : String(summary.count)}
            hint={
              statusView === 'all' ? 'In this list' : `View · ${statusView}`
            }
          />
          <StatCard
            title={t("Revenue")}
            value={loading ? '…' : formatMoney(summary.revenue)}
            hint="Influenced order value"
          />
          <StatCard
            title="ATC"
            value={loading ? '…' : formatCompact(summary.atc)}
            hint="Gifts added to cart"
          />
          <StatCard
            title={t("Used")}
            value={loading ? '…' : formatCompact(summary.used)}
            hint="Redemptions / conversions"
          />
        </InlineGrid>

        <Card padding="0">
          <IndexFilters
            tabs={tabs}
            selected={selectedView}
            onSelect={setSelectedView}
            canCreateNewView={false}
            filters={filters}
            appliedFilters={appliedFilters}
            onClearAll={handleFiltersClearAll}
            mode={mode}
            setMode={setMode}
            queryValue={queryValue}
            queryPlaceholder="Searching in all"
            onQueryChange={setQueryValue}
            onQueryClear={() => {
              setQueryValue('');
              setQuery('');
            }}
            cancelAction={{
              onAction: () => {
                setQueryValue(query);
                setMode(IndexFiltersMode.Default);
              },
              disabled: false,
              loading: false,
            }}
            loading={loading}
          />

          {!loading && filtered.length === 0 ? (
            <EmptyState
              heading={
                items.length === 0
                  ? t('Create your first campaign')
                  : t('No campaigns match your filters')
              }
              action={{
                content:
                  items.length === 0
                    ? t('Create campaign')
                    : t('Clear filters'),
                onAction: () => {
                  if (items.length === 0) navigate('/campaigns/new');
                  else {
                    setSelectedView(0);
                    handleFiltersClearAll();
                  }
                },
              }}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                {items.length === 0
                  ? t('Boost AOV with free gifts, BOGO, and cart goals.')
                  : t('Try another view or clear Status / Type filters.')}
              </p>
            </EmptyState>
          ) : (
            <IndexTable
              resourceName={{
                singular: t('campaign'),
                plural: t('campaigns'),
              }}
              itemCount={filtered.length}
              selectable={false}
              headings={[
                { title: t('Name') },
                { title: t('Type') },
                { title: t('Status') },
                { title: t('Revenue') },
                { title: t('ATC') },
                { title: t('Used') },
                { title: t('Priority') },
                { title: t('Actions') },
              ]}
              loading={loading}
            >
              {filtered.map((c, i) => {
                const busy = actionId === c.id;
                const eff = effectiveStatus(c);
                const displayStatus = statusLabel(eff);
                const usageExhausted = isUsageLimitExhausted(c);
                const pauseLabel =
                  usageExhausted
                    ? t('Limit reached')
                    : eff === 'PAUSED' || c.status === 'PAUSED'
                      ? t('Activate')
                      : t('Pause');
                const revenue = Number(c.stats?.revenue) || 0;
                const atc = Number(c.stats?.atc) || 0;
                const used = Number(c.stats?.used ?? c.usageCount) || 0;

                return (
                  <IndexTable.Row
                    id={c.id}
                    key={c.id}
                    position={i}
                  >
                    <IndexTable.Cell>
                      <Text as="span" fontWeight="semibold">
                        {c.name}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge>{typeLabel(String(c.type || ''))}</Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={statusTone(eff)}>{displayStatus}</Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" variant="bodyMd">
                        {formatMoney(revenue)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" variant="bodyMd">
                        {formatCompact(atc)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" variant="bodyMd">
                        {formatCompact(used)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{c.priority}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <InlineStack gap="200" wrap={false}>
                          <Button
                            size="slim"
                            onClick={() => navigate(campaignEditPath(c))}
                          >
                            {t("Edit")}
                          </Button>
                          <Button
                            size="slim"
                            loading={busy}
                            disabled={
                              busy ||
                              c.status === 'ARCHIVED' ||
                              usageExhausted
                            }
                            onClick={() => void pauseOrResume(c)}
                          >
                            {pauseLabel}
                          </Button>
                          <Button
                            size="slim"
                            tone="critical"
                            disabled={busy}
                            onClick={() => setDeleteTarget(c)}
                          >
                            {t("Delete")}
                          </Button>
                        </InlineStack>
                      </div>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          )}
        </Card>
      </BlockStack>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title={t("Delete campaign?")}
        primaryAction={{
          content: t("Delete"),
          destructive: true,
          loading: actionId === deleteTarget?.id,
          onAction: () => void confirmDelete(),
        }}
        secondaryActions={[
          {
            content: t("Cancel"),
            onAction: () => setDeleteTarget(null),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            {t("Delete")}{' '}
            <Text as="span" fontWeight="semibold">
              {deleteTarget?.name}
            </Text>
            {t("? This archives the campaign so it no longer runs on your storefront.")}
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
